import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import axiosInstance from './axiosInstance';

const humanReadableSize = (sizeInBytes = 0) => {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) return '0 KB';
  const sizeInKb = sizeInBytes / 1024;
  if (sizeInKb < 1024) {
    return `${sizeInKb.toFixed(2)} KB`;
  }
  return `${(sizeInKb / 1024).toFixed(2)} MB`;
};

const parseErrorMessage = async (error) => {
  const fallback = 'Failed to upload the SAP report.';
  if (!error?.response) {
    return error?.message || fallback;
  }

  const { data } = error.response;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error) return parsed.error;
        if (parsed?.message) return parsed.message;
        if (Array.isArray(parsed?.errors) && parsed.errors.length) {
          const first = parsed.errors[0];
          return first?.error || first?.message || fallback;
        }
      } catch (parseErr) {
        return text || fallback;
      }
    } catch (blobErr) {
      return fallback;
    }
  }

  return error.response?.data?.error || error.response?.data?.message || fallback;
};

const SapReportUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadSummary, setUploadSummary] = useState(null);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections?.length) {
      setErrorMessage('Only .csv files up to the configured size limit are supported.');
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;
    setSelectedFile(file);
    setSuccessMessage('');
    setErrorMessage('');
    setUploadSummary(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
    multiple: false,
    onDrop,
  });

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMessage('Please choose an SAP report CSV before uploading.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    setIsUploading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await axiosInstance.post('/api/v1/sap/report', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const summary = response?.data?.data ?? response?.data ?? null;
      setUploadSummary(summary);
      const successCount = Array.isArray(summary?.successful_records)
        ? summary.successful_records.length
        : 0;
      const failedCount = Array.isArray(summary?.failed_records)
        ? summary.failed_records.length
        : 0;
      const totalRows = summary?.total_rows || successCount + failedCount;
      setSuccessMessage(
        `Upload completed. ${successCount} successful, ${failedCount} failed. Total rows: ${totalRows}.`
      );
      setSelectedFile(null);
    } catch (error) {
      const message = await parseErrorMessage(error);
      setErrorMessage(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setSuccessMessage('');
    setErrorMessage('');
    setUploadSummary(null);
  };

  const handleDownloadMatched = () => {
    if (!uploadSummary?.sap_docs_matched?.length) return;
    const rows = uploadSummary.sap_docs_matched.map((item) => ({
      row_number: item.row_number ?? '',
      document_number: item.document_number ?? '',
      supplier_id: item.supplier_id ?? '',
      supplier_name: item.supplier_name ?? '',
      net: item.net ?? '',
      matched_source: item.matched_source ?? '',
    }));
    const headers = [
      'row_number',
      'document_number',
      'supplier_id',
      'supplier_name',
      'net',
      'matched_source',
    ];
    const escapeValue = (value) => {
      const text = String(value ?? '');
      if (text.includes('"') || text.includes(',') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };
    const csvLines = [
      headers.join(','),
      ...rows.map((row) => headers.map((key) => escapeValue(row[key])).join(',')),
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sap_docs_matched.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: 'auto',
        px: '5%',
        py: 4,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'left', width: '100%' }}>
        Upload SAP Report
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Upload the SAP report CSV so it can be stored for reporting.
      </Typography>

      <Box
        {...getRootProps({ className: 'dropzone' })}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: isDragActive ? 'primary.light' : '#fff',
          cursor: 'pointer',
          width: '100%',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
          },
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="h6" gutterBottom>
          {isDragActive ? 'Drop the CSV file here...' : 'Drag & drop the CSV file here'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          or click to select a file
        </Typography>
      </Box>

      {selectedFile && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="info">
            Selected file: {selectedFile.name} ({humanReadableSize(selectedFile.size)})
          </Alert>
        </Box>
      )}

      {successMessage && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="success">{successMessage}</Alert>
        </Box>
      )}

      {errorMessage && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {uploadSummary && Array.isArray(uploadSummary.failed_records)
        && uploadSummary.failed_records.length > 0 && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="info">
            Failed rows ({uploadSummary.failed_records.length}):
          </Alert>
          <Box
            sx={{
              mt: 1,
              maxHeight: 320,
              overflowY: 'auto',
              border: '1px solid #e6e6e6',
              borderRadius: 1,
              px: 2,
              py: 1,
              bgcolor: '#ffffff',
            }}
          >
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {uploadSummary.failed_records.map((item, index) => (
                <Typography variant="body2" key={`${item.row_number || 'unknown'}-${index}`}>
                  Row {item.row_number || 'unknown'}: {item.document_number || 'unknown document'}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={isUploading || !selectedFile}
        >
          {isUploading ? <CircularProgress size={24} color="inherit" /> : 'Upload CSV'}
        </Button>
        {selectedFile && (
          <Button variant="outlined" color="secondary" onClick={handleClear} disabled={isUploading}>
            Clear
          </Button>
        )}
      </Box>

      {uploadSummary && Array.isArray(uploadSummary.sap_docs_matched)
        && uploadSummary.sap_docs_matched.length > 0 && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="info">
            Matched rows (already existed):
          </Alert>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" variant="contained" color="primary" onClick={handleDownloadMatched}>
              Download CSV
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ mt: 1, maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Row Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Document Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Supplier Id</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Supplier Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Net</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Matched Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadSummary.sap_docs_matched.map((item, index) => (
                  <TableRow key={`${item.row_number || 'unknown'}-${index}`}>
                    <TableCell>{item.row_number || 'unknown'}</TableCell>
                    <TableCell>{item.document_number || 'unknown'}</TableCell>
                    <TableCell>{item.supplier_id || 'unknown'}</TableCell>
                    <TableCell>{item.supplier_name || 'unknown'}</TableCell>
                    <TableCell align="right">{item.net ?? ''}</TableCell>
                    <TableCell>{item.matched_source || 'unknown'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default SapReportUpload;
